import { useRoute } from '@react-navigation/native';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity } from 'react-native';

import ActivityList from '@components/ActivityList/ActivityList';
import { ActivityTruncationBanner } from '@components/ActivityList/ActivityTruncationBanner';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { Skeleton } from '@components/shared/Skeleton';
import {
  ActivityRowSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import {
  PANEL_WIDTH,
  PopupActivityFilterPanel,
} from '@components/Web/PopupActivityFilterPanel.web';
import {
  DATES_PANEL_WIDTH,
  PopupTaskFilterProjectDateRangeV3,
} from '@components/Web/PopupTaskFilterProjectV3.web';
import {
  GetTaskDocument,
  GetTaskUnreadAuditsDocument,
  Task,
  useGetTaskUnreadAuditsQuery,
  useMarkAuditsReadMutation,
} from '@graphql/generated';
import { useDebounce } from '@hooks/useDebounce';
import { useListTaskActivityFromQuery } from '@hooks/useListTaskActivityFromQuery';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { TasksStackScreenProps } from '@navigation/tasks/tasks-stack';
import { SEARCH_DEBOUNCE_MS } from '@src/constants/constants';

const PER_PAGE_COUNT = 10;

const renderActivityRowSkeleton = (index: number) => (
  <ActivityRowSkeleton key={index} />
);

export interface TaskActivityLogProps {
  taskID?: Task['id'];
  externalNavigate?: any;
  noMargin?: boolean;
}

export const TaskActivityLog: React.FC<TaskActivityLogProps> = ({
  taskID,
  externalNavigate,
  noMargin = false,
}) => {
  const route = useRoute<TasksStackScreenProps<'task-members'>['route']>();
  const { taskId: routeTaskId = '' } = route.params || {};
  const taskId = routeTaskId || taskID;

  const { t } = useTranslation();
  const [loadingMore, setLoadingMore] = useState(false);

  // ─── Search state ────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const isSearchDebouncing = search !== debouncedSearch;

  // ─── Filter state ────────────────────────────────────────────────────────────
  const [selectedUsers, setSelectedUsers] = useState<ContactType[]>([]);
  const [selectedDueDateRange, setSelectedDueDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  });

  const userIds = useMemo(
    () =>
      selectedUsers.length > 0 ? selectedUsers.map((u) => u.id) : undefined,
    [selectedUsers]
  );

  const startDate = useMemo(
    () =>
      selectedDueDateRange.start
        ? dayjs(selectedDueDateRange.start).startOf('day').toISOString()
        : undefined,
    [selectedDueDateRange.start]
  );

  const endDate = useMemo(
    () =>
      selectedDueDateRange.end
        ? dayjs(selectedDueDateRange.end).endOf('day').toISOString()
        : undefined,
    [selectedDueDateRange.end]
  );

  const {
    activity: allActivity,
    refetch,
    refreshing,
    pageInfo,
    setRefreshing,
    fetchMoreFromCursor,
    loading,
    isTruncated,
  } = useListTaskActivityFromQuery({
    taskId,
    first: PER_PAGE_COUNT,
    term: debouncedSearch || undefined,
    startDate,
    endDate,
    userIds,
    onCompleted: () => setLoadingMore(false),
  });

  // True only up through the very first successful fetch — used to skeleton
  // the filter button and search input themselves so a user can't click one
  // before the activity list has ever appeared. Once flipped, stays true for
  // the rest of the component's lifetime so later debounced searches don't
  // re-lock the input the user is actively typing into.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);
  const isInitialMountLoading = !hasLoadedOnce && loading;

  // listActivitiesByTask's cache field policy keys on `taskId` alone (see
  // initializeCache.ts), not on `term` — so switching search terms doesn't
  // get its own cache slot. cache-and-network's synchronous "cache" phase
  // can return the PREVIOUS term's still-cached results the instant
  // `debouncedSearch` changes (isSearchDebouncing flips false before the new
  // term's network response lands), which `loading && allActivity.length
  // === 0` alone would miss since allActivity is non-empty — Copilot
  // review. Track which term the currently-held allActivity was actually
  // loaded for, and keep skeletoning until they match.
  const [loadedForTerm, setLoadedForTerm] = useState<string | undefined>(
    undefined
  );
  useEffect(() => {
    if (!loading) setLoadedForTerm(debouncedSearch || undefined);
  }, [loading, debouncedSearch]);
  const isStaleTermResult = loadedForTerm !== (debouncedSearch || undefined);

  // Skeleton the list body both on first mount and during the search
  // debounce window — the list is replaced with placeholders while the
  // debounced term is still catching up to what the user typed, then again
  // while that term's network request is in flight, and until the loaded
  // results actually match the current term (see isStaleTermResult above).
  const showActivitySkeleton =
    isSearchDebouncing ||
    isStaleTermResult ||
    (loading && allActivity.length === 0);

  // TA-10818: the hot-path getTask query (shared with TaskDetail) only
  // carries unreadAuditsCount now, not the full unbounded list — this
  // screen is the one place that actually needs the ids and the name, so
  // it fetches them on its own, only when opened.
  const { data: taskInfo } = useGetTaskUnreadAuditsQuery({
    variables: { id: taskId || '' },
    skip: !taskId,
    // network-only: this query exists only to feed markAuditsRead below with the
    // current ids -- the global cache-and-network default would resolve this
    // twice (once from a warm cache, once from the network), and the effect
    // below is keyed on the array reference, so a stale cache hit could fire
    // markAuditsRead for already-read ids before the real result lands
    // (Copilot review, PR #5905).
    fetchPolicy: 'network-only',
  });

  const [markAuditsRead] = useMarkAuditsReadMutation({
    refetchQueries: [
      { query: GetTaskDocument, variables: { id: taskId } },
      // Otherwise the cached unread-audit ids survive the mark-read call,
      // and a later mount can briefly see the stale non-empty list before
      // the network refetch lands and re-fires markAuditsRead for ids the
      // backend already marked read (Copilot review, PR #5905).
      { query: GetTaskUnreadAuditsDocument, variables: { id: taskId } },
    ],
  });

  useEffect(() => {
    if ((taskInfo?.getTask?.unreadAudits?.length ?? 0) > 0) {
      markAuditsRead({
        variables: {
          attributes: {
            auditIds: taskInfo?.getTask?.unreadAudits?.map((a) => a.id),
          },
        },
      });
    }
  }, [taskId, taskInfo?.getTask?.unreadAudits, markAuditsRead]);

  // Re-fetch on tab focus to recover from stale data after a WebSocket idle
  // disconnect.
  useEffect(() => {
    const handleFocus = () => refetch();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  // ─── Filter panel popup state ─────────────────────────────────────────────
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterPanel, setFilterPanel] = useState<'main' | 'dates'>('main');
  const [mouseOverFilter, setMouseOverFilter] = useState(false);
  const [userSectionInitialOpen, setUserSectionInitialOpen] = useState(true);

  const filterButtonRef = useOutsideClick<HTMLDivElement>(
    useCallback((event: MouseEvent) => {
      const rect = filterPanelRef.current?.getBoundingClientRect();
      const inPanel =
        rect &&
        event.x > rect.x &&
        event.x < rect.x + rect.width &&
        event.y > rect.y &&
        event.y < rect.y + rect.height;
      if (!inPanel) {
        setFilterVisible(false);
        setFilterPanel('main');
      }
    }, [])
  );
  const filterPanelRef = useRef<HTMLDivElement>();

  // Both popup panels are fixed-width (main = PANEL_WIDTH, dates =
  // DATES_PANEL_WIDTH), so we right-align them to the filter button using the
  // constant instead of rendering an off-screen copy on every page load just
  // to measure. (onLayout does not fire reliably for a child inside the
  // react-native-web Modal portal, so runtime measurement is not viable here.)
  const panelWidth = filterPanel === 'dates' ? DATES_PANEL_WIDTH : PANEL_WIDTH;

  const filterBounds = (() => {
    if (!filterButtonRef.current) return undefined;
    const client = filterButtonRef.current.getBoundingClientRect();
    return {
      top: client.top + client.height + 4,
      left: client.left + client.width - panelWidth,
    };
  })();

  const clearAllFilters = () => {
    setSelectedUsers([]);
    setSelectedDueDateRange({ start: undefined, end: undefined });
  };

  const badgeNumber =
    selectedUsers.length +
    (selectedDueDateRange.start || selectedDueDateRange.end ? 1 : 0);

  const onLoadMore = () => {
    setLoadingMore(true);
    fetchMoreFromCursor();
  };

  const renderActivityListFooter = useCallback(
    () => (
      <>
        {pageInfo?.hasNextPage ? (
          <Box mb='listFooter'>
            {loadingMore && <ActivityIndicatorLoading />}
          </Box>
        ) : (
          <Box mb='l' />
        )}
      </>
    ),
    [pageInfo?.hasNextPage, loadingMore]
  );

  return (
    <Box flex={1} backgroundColor='white'>
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <Box
        borderBottomWidth={1}
        borderBottomColor='grey02'
        flexDirection='row'
        alignItems='center'
        py='m'>
        {/* Search input */}
        <Box flex={1} mr='xs'>
          {isInitialMountLoading ? (
            <Skeleton
              testID='task-activity-search-skeleton'
              variant='rect'
              width='100%'
              height={36}
              borderRadius='xs'
            />
          ) : (
            <SearchInput
              height={36}
              onTextChanged={setSearch}
              value={search}
              onCancel={() => setSearch('')}
            />
          )}
        </Box>

        {/* Filter button */}
        {isInitialMountLoading ? (
          <Skeleton
            testID='task-activity-filter-skeleton'
            variant='rect'
            width={76}
            height={36}
            borderRadius='xs'
          />
        ) : (
          <Box ref={filterButtonRef}>
            <TouchableOpacity
              accessibilityLabel={t('shared:filter')}
              onPress={() => {
                setFilterVisible((v) => !v);
                setFilterPanel('main');
              }}>
              <Box
                height={36}
                flexDirection='row'
                py='xs'
                px='m'
                onMouseEnter={() => setMouseOverFilter(true)}
                onMouseLeave={() => setMouseOverFilter(false)}
                backgroundColor={
                  mouseOverFilter || badgeNumber > 0 || filterVisible
                    ? 'grey01'
                    : 'white'
                }
                borderRadius='xs'
                alignItems='center'
                justifyContent='center'>
                <Icon variant='s' name='AlignCenterDesc' color='black' />
                <Text marginLeft='xs' variant='labelSmall' color='textPrimary'>
                  {t('shared:filter')}
                </Text>
                {badgeNumber > 0 && (
                  <Box
                    marginLeft='xs'
                    minWidth={20}
                    height={20}
                    alignItems='center'
                    justifyContent='center'
                    backgroundColor='greenSecondary'
                    borderRadius='s'>
                    <Text variant='webBodyHintSubline' color='white'>
                      {badgeNumber}
                    </Text>
                  </Box>
                )}
                {badgeNumber > 0 && (
                  <Box>
                    <Icon
                      marginLeft='xs'
                      variant='xxxs'
                      name='Close'
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        clearAllFilters();
                      }}
                      color='black'
                    />
                  </Box>
                )}
              </Box>
            </TouchableOpacity>
          </Box>
        )}

        {/* Filter panel modal */}
        <Modal
          visible={filterVisible}
          transparent
          onDismiss={() => {
            setFilterVisible(false);
            setFilterPanel('main');
            setUserSectionInitialOpen(true);
          }}
          onRequestClose={() => {
            setFilterVisible(false);
            setFilterPanel('main');
            setUserSectionInitialOpen(true);
          }}>
          <Box
            ref={filterPanelRef}
            style={{ left: filterBounds?.left, top: filterBounds?.top }}
            width={panelWidth}>
            <Box flexShrink={1}>
              {filterPanel === 'main' ? (
                <PopupActivityFilterPanel
                  showProjects={false}
                  initialUserOpen={userSectionInitialOpen}
                  selectedProjects={[]}
                  onSelectProject={() => {}}
                  selectedUsers={selectedUsers}
                  onSelectUser={setSelectedUsers}
                  selectedDueDateRange={selectedDueDateRange}
                  onPressSelectDates={() => setFilterPanel('dates')}
                  onClearAll={clearAllFilters}
                />
              ) : (
                <PopupTaskFilterProjectDateRangeV3
                  selectedDueDateRange={selectedDueDateRange}
                  onSelectDueDateRange={(dr) => {
                    setSelectedDueDateRange(dr);
                    setFilterPanel('main');
                    setUserSectionInitialOpen(false);
                  }}
                  isRangeSelector={true}
                />
              )}
            </Box>
          </Box>
        </Modal>
      </Box>

      {/* ─── Activity list ──────────────────────────────────────────────────── */}
      {showActivitySkeleton ? (
        <Box mb='listFooter'>
          {/* Matches ActivityList.web.tsx's styles.dateHeader — a full-width
              grey01 band, not a bare text line — so the skeleton doesn't
              swap to a completely different header shape once real data
              loads. */}
          <Box backgroundColor='grey01' py='xs' px='m' mb='m'>
            <Skeleton variant='text' width={80} />
          </Box>
          <SkeletonList row={renderActivityRowSkeleton} />
        </Box>
      ) : (
        <>
          {isTruncated && <ActivityTruncationBanner />}
          <ActivityList
            externalNavigate={externalNavigate}
            activityType='Task'
            filterValForGlobalActivity={search || undefined}
            refreshing={refreshing}
            audits={allActivity}
            taskId={taskId}
            noMargin={noMargin}
            onRefresh={() => {
              setRefreshing(true);
              // Apollo bails out of emitting a new `data`/`error` reference
              // when a refetch resolves to content deep-equal to what's
              // already cached (e.g. no new activity since the last check),
              // so the hook's data/error effects never fire and refreshing
              // is left stuck true. Resolving/rejecting this promise is the
              // only signal that's guaranteed regardless of Apollo's cache
              // diffing (TA-10476 QA follow-up).
              refetch()
                .catch(() => {})
                .finally(() => setRefreshing(false));
            }}
            onEndReached={onLoadMore}
            renderEmptyActiveComponent={() => (
              <Box
                paddingTop='m'
                alignItems='center'
                alignSelf='center'
                alignContent='center'
                justifyContent='center'>
                <Text color='grey04' textAlign='center'>
                  {search
                    ? t('shared:noResultsFoundForTerm', { term: search })
                    : t('shared:NoRecordFound')}
                </Text>
              </Box>
            )}
            listFooterComponent={renderActivityListFooter}
          />
        </>
      )}
    </Box>
  );
};
